// Nội dung vi/en cho trang lỗi. Ứng dụng chưa có thư viện i18n, nên bảng dịch
// nằm ở đây và ngôn ngữ được chọn từ ?lang= hoặc ngôn ngữ trình duyệt.

export type ErrorLang = "vi" | "en";

export type ErrorReason =
  | "session_expired"
  | "workspace_not_found"
  | "access_denied"
  | "auth_retry"
  | "auth_failed"
  | "workspace_mismatch"
  | "workspace_unavailable";

export type ErrorAction =
  | "login"
  | "retry_return_to"
  | "retry_login"
  | "switch_account"
  | "switch_workspace"
  | "home";

export interface ReasonCopy {
  title: string;
  description: string;
  hint: string;
  footer: string;
}

export interface GenericCopy {
  title: string;
  description: string;
}

export interface ErrorUiCopy {
  login: string;
  retry: string;
  back: string;
  home: string;
  logout: string;
  switchAccount: string;
  switchWorkspace: string;
  homeLong: string;
  generalFooter: string;
  technicalDetail: string;
  codeLabel: string;
  unknownTitle: string;
  unknownDescription: string;
  routeErrorTitle: string;
  routeErrorDescription: string;
}

export interface ErrorCopyBundle {
  ui: ErrorUiCopy;
  generic: Record<404 | 500 | 502 | 503, GenericCopy>;
  reasons: Record<ErrorReason, ReasonCopy>;
}

export const REASON_ACTIONS: Record<ErrorReason, ErrorAction[]> = {
  session_expired: ["login", "switch_account", "home"],
  workspace_not_found: ["retry_return_to", "switch_account", "home"],
  access_denied: ["switch_account", "switch_workspace", "home"],
  auth_retry: ["retry_login", "switch_account", "home"],
  auth_failed: ["login", "switch_account", "home"],
  workspace_mismatch: ["switch_workspace", "switch_account", "home"],
  workspace_unavailable: ["retry_return_to", "switch_account", "home"],
};

const vi: ErrorCopyBundle = {
  ui: {
    login: "Đăng nhập lại",
    retry: "Thử lại",
    back: "Quay lại",
    home: "Trang chủ",
    logout: "Đăng xuất",
    switchAccount: "Đăng xuất để chuyển tài khoản",
    switchWorkspace: "Chọn không gian làm việc khác",
    homeLong: "Về trang chủ",
    generalFooter: "Nếu lỗi này tiếp tục xảy ra, vui lòng liên hệ bộ phận hỗ trợ.",
    technicalDetail: "Chi tiết kỹ thuật",
    codeLabel: "Mã lỗi",
    unknownTitle: "Lỗi không xác định",
    unknownDescription: "Đã có lỗi không mong muốn xảy ra. Vui lòng thử lại sau.",
    routeErrorTitle: "Foxia đang gặp sự cố",
    routeErrorDescription:
      "Trang này chưa tải được đầy đủ. Vui lòng thử lại hoặc quay về trang chủ Foxia.",
  },
  generic: {
    404: {
      title: "Không tìm thấy trang",
      description: "Trang bạn đang tìm kiếm không tồn tại hoặc đã bị di chuyển.",
    },
    500: {
      title: "Lỗi hệ thống",
      description:
        "Có lỗi xảy ra từ phía chúng tôi. Chúng tôi đang nỗ lực khắc phục.",
    },
    502: {
      title: "Lỗi kết nối",
      description:
        "Máy chủ hiện tại không thể kết nối. Foxia đang bận kiểm tra hệ thống.",
    },
    503: {
      title: "Không gian làm việc chưa sẵn sàng",
      description:
        "Không gian làm việc hiện không hoạt động. Bạn có thể thử lại, về trang chủ hoặc đăng xuất.",
    },
  },
  reasons: {
    session_expired: {
      title: "Phiên đăng nhập đã hết hạn",
      description:
        "Vì lý do bảo mật, phiên làm việc của bạn đã kết thúc. Đăng nhập lại để tiếp tục sử dụng Foxia.",
      hint: "Dữ liệu của bạn vẫn an toàn. Bạn có thể đăng nhập lại hoặc chuyển sang tài khoản khác.",
      footer: "Nếu bạn vẫn gặp vấn đề, hãy thử đăng nhập lại sau vài phút.",
    },
    workspace_not_found: {
      title: "Không tìm thấy không gian làm việc",
      description:
        "Bạn đã đăng nhập thành công nhưng tài khoản này chưa được gán vào không gian làm việc nào trên Foxia.",
      hint: "Hãy liên hệ quản trị viên để được thêm vào không gian làm việc, hoặc đăng xuất để chuyển sang tài khoản khác.",
      footer:
        "Nếu bạn vừa được thêm vào không gian làm việc, hãy thử lại sau vài phút.",
    },
    access_denied: {
      title: "Bạn chưa được cấp quyền truy cập",
      description:
        "Bạn chưa được cấp quyền dùng ứng dụng này trong không gian làm việc, hoặc yêu cầu truy cập đã bị từ chối.",
      hint: "Hãy liên hệ quản trị viên không gian làm việc để được cấp quyền, hoặc chuyển sang tài khoản hay không gian làm việc khác.",
      footer: "Nếu bạn cho rằng đây là nhầm lẫn, hãy báo cho quản trị viên của bạn.",
    },
    auth_retry: {
      title: "Chưa thể hoàn tất đăng nhập",
      description:
        "Hệ thống đăng nhập tạm thời chưa phản hồi. Đây không phải lỗi từ phía bạn.",
      hint: "Vui lòng thử lại sau ít phút.",
      footer: "Nếu lỗi tiếp tục xảy ra, vui lòng liên hệ bộ phận hỗ trợ.",
    },
    auth_failed: {
      title: "Không thể hoàn tất đăng nhập",
      description:
        "Yêu cầu đăng nhập không hợp lệ hoặc đã được sử dụng. Hãy đăng nhập lại để tiếp tục.",
      hint: "Hãy tránh mở nhiều tab đăng nhập cùng lúc hoặc dùng lại liên kết cũ.",
      footer: "Nếu bạn vẫn gặp vấn đề, vui lòng liên hệ bộ phận hỗ trợ.",
    },
    workspace_mismatch: {
      title: "Không đúng không gian làm việc",
      description:
        "Tài khoản này không thuộc không gian làm việc của địa chỉ bạn đang truy cập.",
      hint: "Hãy chọn đúng không gian làm việc hoặc đăng nhập bằng tài khoản khác.",
      footer: "Nếu bạn cần quyền truy cập, hãy liên hệ quản trị viên.",
    },
    workspace_unavailable: {
      title: "Hệ thống ERP của không gian làm việc đang không truy cập được",
      description:
        "Chúng tôi chưa thể kết nối tới hệ thống của không gian làm việc này. Có thể hệ thống đang khởi động lại hoặc bảo trì.",
      hint: "Dữ liệu của bạn vẫn an toàn. Vui lòng thử lại sau ít phút.",
      footer: "Nếu lỗi kéo dài, hãy báo quản trị viên hoặc bộ phận hỗ trợ.",
    },
  },
};

const en: ErrorCopyBundle = {
  ui: {
    login: "Sign in again",
    retry: "Try again",
    back: "Go back",
    home: "Home",
    logout: "Sign out",
    switchAccount: "Sign out to switch account",
    switchWorkspace: "Choose another workspace",
    homeLong: "Back to home",
    generalFooter: "If this keeps happening, please contact support.",
    technicalDetail: "Technical details",
    codeLabel: "Error code",
    unknownTitle: "Unknown error",
    unknownDescription: "An unexpected error occurred. Please try again later.",
    routeErrorTitle: "Foxia is having trouble",
    routeErrorDescription:
      "This page could not be loaded completely. Please try again or go back to the Foxia home page.",
  },
  generic: {
    404: {
      title: "Page not found",
      description: "The page you are looking for does not exist or has been moved.",
    },
    500: {
      title: "System error",
      description: "Something went wrong on our side. We are working on a fix.",
    },
    502: {
      title: "Connection error",
      description:
        "We cannot reach the server right now. Foxia is checking the system.",
    },
    503: {
      title: "Workspace not ready",
      description:
        "The workspace is not running right now. You can try again, go home or sign out.",
    },
  },
  reasons: {
    session_expired: {
      title: "Your session has expired",
      description:
        "For security reasons your session has ended. Sign in again to keep using Foxia.",
      hint: "Your data is safe. You can sign in again or switch to another account.",
      footer: "If the problem persists, try signing in again in a few minutes.",
    },
    workspace_not_found: {
      title: "Workspace not found",
      description:
        "You signed in successfully, but this account has not been added to any Foxia workspace.",
      hint: "Ask an administrator to add you to a workspace, or sign out to use another account.",
      footer: "If you were just added to a workspace, try again in a few minutes.",
    },
    access_denied: {
      title: "You do not have access yet",
      description:
        "You have not been granted access to this app in the workspace, or the access request was declined.",
      hint: "Contact your workspace administrator to get access, or switch to another account or workspace.",
      footer: "If you think this is a mistake, let your administrator know.",
    },
    auth_retry: {
      title: "Sign-in could not be completed",
      description:
        "The sign-in service is temporarily not responding. This is not caused by you.",
      hint: "Please try again in a few minutes.",
      footer: "If the error continues, please contact support.",
    },
    auth_failed: {
      title: "Unable to complete sign-in",
      description:
        "The sign-in request is invalid or was already used. Sign in again to continue.",
      hint: "Avoid opening several sign-in tabs at once or reusing an old link.",
      footer: "If the problem persists, please contact support.",
    },
    workspace_mismatch: {
      title: "Wrong workspace",
      description:
        "This account does not belong to the workspace at the address you are visiting.",
      hint: "Choose the right workspace or sign in with another account.",
      footer: "If you need access, contact an administrator.",
    },
    workspace_unavailable: {
      title: "The workspace ERP system is unreachable",
      description:
        "We cannot connect to this workspace's system right now. It may be restarting or under maintenance.",
      hint: "Your data is safe. Please try again in a few minutes.",
      footer: "If this lasts, tell your administrator or contact support.",
    },
  },
};

const BUNDLES: Record<ErrorLang, ErrorCopyBundle> = { vi, en };

export const GENERIC_STATUS_CODES = [404, 500, 502, 503] as const;

const LANG_STORAGE_KEY = "foxia_lang";

function asErrorLang(value: string | null | undefined): ErrorLang | null {
  const normalized = value?.toLowerCase();
  return normalized === "vi" || normalized === "en" ? normalized : null;
}

/**
 * Ngôn ngữ trang lỗi: ?lang= thắng, sau đó đến lựa chọn đã lưu (localStorage
 * `foxia_lang`), mặc định tiếng Việt vì toàn bộ ứng dụng hiện là tiếng Việt.
 */
export function resolveErrorLang(search: string): ErrorLang {
  const fromQuery = asErrorLang(new URLSearchParams(search).get("lang"));
  if (fromQuery) return fromQuery;
  try {
    if (typeof localStorage !== "undefined") {
      const stored = asErrorLang(localStorage.getItem(LANG_STORAGE_KEY));
      if (stored) return stored;
    }
  } catch {
    // Trình duyệt chặn localStorage: dùng mặc định.
  }
  return "vi";
}

export function getErrorCopy(lang: ErrorLang): ErrorCopyBundle {
  return BUNDLES[lang];
}

export function isErrorReason(value: string | null): value is ErrorReason {
  return value !== null && Object.prototype.hasOwnProperty.call(REASON_ACTIONS, value);
}

/**
 * Chuyển mã lỗi OAuth (Foxia ID/Hydra) hoặc mã lỗi của gateway thành reason
 * của trang lỗi. Mã lạ rơi vào auth_failed để người dùng luôn có đường thoát.
 */
export function reasonForAuthError(code: string): ErrorReason {
  const normalized = code.trim().toLowerCase();
  if (normalized.startsWith("failed_to_") || normalized.startsWith("no_id_token")) {
    return "auth_retry";
  }
  switch (normalized) {
    case "access_denied":
    case "consent_required":
      return "access_denied";
    case "login_required":
    case "interaction_required":
    case "request_expired":
    case "auth_state_expired":
    case "missing_auth_state":
    case "invalid_auth_state":
    case "token_expired":
    case "missing_token":
    case "invalid_token":
      return "session_expired";
    case "server_error":
    case "temporarily_unavailable":
    case "exchange_failed":
    case "silent_auth_unavailable":
      return "auth_retry";
    case "domain_mismatch":
    case "workspace_does_not_match_domain":
      return "workspace_mismatch";
    default:
      return "auth_failed";
  }
}

/** Đường dẫn trang lỗi cho một mã lỗi đăng nhập; chỉ mang mã, không mang mô tả thô. */
export function authErrorPagePath(code: string, returnTo?: string): string {
  const query = new URLSearchParams({
    reason: reasonForAuthError(code),
    error: code.slice(0, 64),
  });
  if (returnTo) query.set("return_to", returnTo);
  return `/error?${query.toString()}`;
}
